import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { debounce, fetchConfig } from '@theme/utilities';

export const FUNDRAISER_CODE = 'DBHSXC';
export const ATTR_CODE = 'Fundraiser Code';
export const ATTR_STUDENT = 'Student Name';
export const FUNDRAISER_STUDENT_REQUIRED_MESSAGE = "Please enter the student's name to continue to checkout.";

/**
 * @param {string | undefined} url
 * @param {string} fallback
 * @returns {string}
 */
function cartJsEndpoint(url, fallback) {
  const value = url || fallback;
  return value.endsWith('.js') ? value : `${value}.js`;
}

function cartUpdateUrl() {
  return cartJsEndpoint(Theme?.routes?.cart_update_url, '/cart/update.js');
}

function cartChangeUrl() {
  return cartJsEndpoint(Theme?.routes?.cart_change_url, '/cart/change.js');
}

function cartAddUrl() {
  return cartJsEndpoint(Theme?.routes?.cart_add_url, '/cart/add.js');
}

function cartUrl() {
  return cartJsEndpoint(Theme?.routes?.cart_url, '/cart.js');
}

/**
 * @param {string} value
 * @returns {string}
 */
export function normalizeFundraiserCode(value) {
  return (value || '').trim().toUpperCase();
}

/**
 * @param {string} value
 * @returns {boolean}
 */
export function isValidFundraiserCode(value) {
  return normalizeFundraiserCode(value) === FUNDRAISER_CODE;
}

/**
 * @param {Record<string, string> | null | undefined} existing
 * @param {string} code
 * @param {string} studentName
 * @returns {Record<string, string>}
 */
function mergeLineProperties(existing, code, studentName) {
  /** @type {Record<string, string>} */
  const properties = {};

  if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
    for (const [key, value] of Object.entries(existing)) {
      if (key === ATTR_CODE || key === ATTR_STUDENT) continue;
      if (value == null || value === '') continue;
      properties[key] = String(value);
    }
  }

  if (code) {
    properties[ATTR_CODE] = code;
    properties[ATTR_STUDENT] = studentName;
  }

  return properties;
}

/** @type {Promise<any>} */
let saveQueue = Promise.resolve();

/**
 * Saves fundraiser details as cart attributes AND line-item properties.
 * Line-item properties are what Shopify reliably copies onto the order.
 * @param {string} code
 * @param {string} studentName
 * @param {AbortSignal} [signal]
 * @returns {Promise<any>}
 */
export async function saveFundraiserAttributes(code, studentName, signal) {
  const run = () => persistFundraiserDetails(code, studentName, signal);
  const pending = saveQueue.then(run, run);
  saveQueue = pending.catch(() => {});
  return pending;
}

/**
 * @param {string} code
 * @param {string} studentName
 * @param {AbortSignal} [signal]
 * @returns {Promise<any>}
 */
async function persistFundraiserDetails(code, studentName, signal) {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const normalizedCode = isValidFundraiserCode(code) ? FUNDRAISER_CODE : '';
  const normalizedStudent = normalizedCode ? (studentName || '').trim() : '';

  const updateResponse = await fetch(cartUpdateUrl(), {
    ...fetchConfig('json', {
      body: JSON.stringify({
        attributes: {
          [ATTR_CODE]: normalizedCode,
          [ATTR_STUDENT]: normalizedStudent,
        },
      }),
    }),
    signal,
  });

  if (!updateResponse.ok) {
    throw new Error(`Unable to save fundraiser details (${updateResponse.status})`);
  }

  let cart = await updateResponse.json();
  if (cart.errors) {
    throw new Error(typeof cart.errors === 'string' ? cart.errors : 'Unable to save fundraiser details');
  }

  const items = Array.isArray(cart.items) ? cart.items : [];

  for (const item of items) {
    const nextProperties = mergeLineProperties(item.properties, normalizedCode, normalizedStudent);
    const currentProperties =
      item.properties && typeof item.properties === 'object' && !Array.isArray(item.properties)
        ? item.properties
        : {};
    const currentCode = String(currentProperties[ATTR_CODE] || '');
    const currentStudent = String(currentProperties[ATTR_STUDENT] || '');

    if (currentCode === normalizedCode && currentStudent === normalizedStudent) continue;

    const changeResponse = await fetch(cartChangeUrl(), {
      ...fetchConfig('json', {
        body: JSON.stringify({
          id: item.key || item.id,
          quantity: item.quantity,
          properties: nextProperties,
        }),
      }),
      signal,
    });

    if (!changeResponse.ok) {
      throw new Error(`Unable to save fundraiser details on cart items (${changeResponse.status})`);
    }

    cart = await changeResponse.json();
    if (cart.errors) {
      throw new Error(typeof cart.errors === 'string' ? cart.errors : 'Unable to save fundraiser details on cart items');
    }
  }

  const savedCode = String(cart.attributes?.[ATTR_CODE] || '').trim();
  if (normalizedCode && savedCode !== normalizedCode) {
    throw new Error('Fundraiser details did not save to the cart');
  }

  return cart;
}

/**
 * @returns {Promise<{attributes?: Record<string, string>, items?: any[]}>}
 */
export async function fetchCart() {
  const response = await fetch(cartUrl());
  if (!response.ok) throw new Error('Unable to load cart');
  return response.json();
}

/**
 * @param {HTMLFormElement | null} form
 * @returns {{id: number, quantity: number} | null}
 */
function getProductFormItem(form) {
  if (!(form instanceof HTMLFormElement)) return null;
  const idInput = form.querySelector('[name="id"]');
  const quantityInput = form.querySelector('[name="quantity"]');
  const id = Number(idInput instanceof HTMLInputElement ? idInput.value : 0);
  const quantity = Number(quantityInput instanceof HTMLInputElement ? quantityInput.value : 1) || 1;
  if (!id) return null;
  return { id, quantity };
}

/**
 * Adds the current product to the cart, then sends the customer to checkout.
 * Cart attributes and line-item properties are saved first so they appear on the order.
 * @param {HTMLFormElement | null} form
 * @param {string} code
 * @param {string} studentName
 */
export async function addProductAndCheckout(form, code, studentName) {
  const normalizedCode = isValidFundraiserCode(code) ? FUNDRAISER_CODE : '';
  const normalizedStudent = normalizedCode ? (studentName || '').trim() : '';

  if (normalizedCode) {
    await saveFundraiserAttributes(normalizedCode, normalizedStudent);
  }

  const item = getProductFormItem(form);
  if (item) {
    /** @type {{id: number, quantity: number, properties?: Record<string, string>}} */
    const lineItem = {
      id: item.id,
      quantity: item.quantity,
    };

    if (normalizedCode) {
      lineItem.properties = mergeLineProperties(null, normalizedCode, normalizedStudent);
    }

    const response = await fetch(cartAddUrl(), {
      ...fetchConfig('json', {
        body: JSON.stringify({ items: [lineItem] }),
      }),
    });

    if (!response.ok) {
      /** @type {{description?: string, message?: string}} */
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || 'Unable to add this product to checkout');
    }
  }

  window.location.assign('/checkout');
}

function getCartFundraiserState() {
  const components = document.querySelectorAll('fundraiser-code-component');
  for (const component of components) {
    if (!(component instanceof FundraiserCodeComponent)) continue;
    const code = component.getEnteredCode();
    const applied = component.isApplied() || isValidFundraiserCode(code);
    if (applied) {
      return {
        applied: true,
        studentName: component.getStudentName(),
        component,
      };
    }
  }
  return { applied: false, studentName: '', component: null };
}

function enableCheckoutButtons() {
  document.querySelectorAll('#checkout, .cart__checkout-button').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.closest('.shipping-insurance-upsell')) return;
    button.disabled = false;
    button.removeAttribute('aria-disabled');
  });
  document.querySelectorAll('.additional-checkout-buttons, shopify-accelerated-checkout-cart').forEach((element) => {
    element.classList.remove('fundraiser-checkout-blocked');
    element.removeAttribute('aria-disabled');
  });
}

function disableCheckoutButtons() {
  document.querySelectorAll('#checkout, .cart__checkout-button').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.closest('.shipping-insurance-upsell')) return;
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
  });
  document.querySelectorAll('.additional-checkout-buttons, shopify-accelerated-checkout-cart').forEach((element) => {
    element.classList.add('fundraiser-checkout-blocked');
    element.setAttribute('aria-disabled', 'true');
  });
}

let checkoutGuardBound = false;
let checkoutInProgress = false;

function bindCheckoutGuard() {
  if (checkoutGuardBound) return;
  checkoutGuardBound = true;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const checkoutButton = target.closest('#checkout, .cart__checkout-button');
      if (!checkoutButton) return;
      if (checkoutButton.closest('.shipping-insurance-upsell')) return;

      const state = getCartFundraiserState();
      if (!state.applied) return;
      if (state.studentName) return;

      event.preventDefault();
      event.stopPropagation();
      state.component?.showStudentRequiredError();
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('.additional-checkout-buttons, shopify-accelerated-checkout-cart')) return;

      const state = getCartFundraiserState();
      if (!state.applied) return;
      if (state.studentName) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      state.component?.showStudentRequiredError();
    },
    true
  );

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const isCartCheckout = form.id === 'cart-form';
      const isInsuranceCheckout = Boolean(form.querySelector('input[name="return_to"][value="/checkout"]'));
      if (!isCartCheckout && !isInsuranceCheckout) return;

      const state = getCartFundraiserState();
      if (!state.applied) return;

      event.preventDefault();
      event.stopPropagation();

      if (!state.studentName) {
        state.component?.showStudentRequiredError();
        return;
      }

      if (checkoutInProgress) return;
      checkoutInProgress = true;

      try {
        await saveFundraiserAttributes(FUNDRAISER_CODE, state.studentName);
      } catch (error) {
        checkoutInProgress = false;
        console.error(error);
        state.component?.showSaveError();
        return;
      }

      if (isInsuranceCheckout) {
        form.submit();
        return;
      }

      window.location.assign('/checkout');
    },
    true
  );
}

/**
 * @typedef {object} FundraiserCodeRefs
 * @property {HTMLInputElement} codeInput
 * @property {HTMLElement} successMessage
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} studentField
 * @property {HTMLInputElement} studentInput
 * @property {HTMLElement} [studentError]
 * @property {HTMLInputElement} [hiddenCode]
 * @property {HTMLInputElement} [hiddenStudent]
 * @property {HTMLButtonElement} [applyButton]
 *
 * @extends {Component<FundraiserCodeRefs>}
 */
class FundraiserCodeComponent extends Component {
  requiredRefs = ['codeInput', 'successMessage', 'errorMessage', 'studentField', 'studentInput'];

  /** @type {boolean} */
  #saving = false;

  /** @type {AbortController | null} */
  #activeSave = null;

  connectedCallback() {
    super.connectedCallback();
    bindCheckoutGuard();
    this.#syncCheckoutState();
    document.addEventListener('cart:update', this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:update', this.#onCartUpdate);
    this.#debouncedSave.cancel();
  }

  isApplied() {
    return this.dataset.applied === 'true';
  }

  getEnteredCode() {
    return this.refs.codeInput?.value || '';
  }

  getStudentName() {
    return this.refs.studentInput?.value.trim() || '';
  }

  showStudentRequiredError() {
    this.refs.studentField.classList.remove('hidden');
    this.refs.studentError?.classList.remove('hidden');
    if (this.refs.studentError) this.refs.studentError.textContent = FUNDRAISER_STUDENT_REQUIRED_MESSAGE;
    this.refs.studentInput.focus();
    this.#syncCheckoutState();
  }

  showSaveError() {
    this.refs.errorMessage.textContent = 'Could not save fundraiser details. Please try Apply again.';
    this.refs.errorMessage.classList.remove('hidden');
    this.refs.successMessage.classList.add('hidden');
  }

  applyCode = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const code = this.refs.codeInput.value;
    this.#hideMessages();

    if (!isValidFundraiserCode(code)) {
      this.#showUnrecognized();
      if (this.isApplied()) await this.#clearFundraiser(true);
      return;
    }

    await this.#applyFundraiser();
  };

  handleCodeInput = () => {
    this.#hideMessages();
    if (this.isApplied() && !isValidFundraiserCode(this.refs.codeInput.value)) {
      this.#clearFundraiser(true);
    }
  };

  handleStudentInput = () => {
    this.refs.studentError?.classList.add('hidden');
    this.#writeHiddenAttributes(FUNDRAISER_CODE, this.getStudentName());
    this.#syncCheckoutState();
    this.#saveStudentName();
  };

  handleStudentBlur = () => {
    this.#debouncedSave.cancel();
    this.#saveStudentName();
  };

  #debouncedSave = debounce(() => {
    this.#saveStudentName();
  }, 250);

  async #applyFundraiser() {
    const studentName = this.getStudentName();
    this.#setAppliedUi(studentName);

    try {
      await saveFundraiserAttributes(FUNDRAISER_CODE, studentName);
    } catch (error) {
      console.error(error);
      this.showSaveError();
    }
  }

  async #saveStudentName() {
    if (!this.isApplied()) return;
    if (this.#activeSave) this.#activeSave.abort();

    const abortController = new AbortController();
    this.#activeSave = abortController;
    this.#saving = true;

    try {
      await saveFundraiserAttributes(FUNDRAISER_CODE, this.getStudentName(), abortController.signal);
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.error(error);
    } finally {
      if (this.#activeSave === abortController) {
        this.#activeSave = null;
        this.#saving = false;
      }
    }
  }

  async #clearFundraiser(keepCodeValue = false) {
    this.#setClearedUi(keepCodeValue);
    try {
      await saveFundraiserAttributes('', '');
    } catch (error) {
      console.error(error);
    }
  }

  #setAppliedUi(studentName) {
    this.dataset.applied = 'true';
    this.refs.codeInput.value = FUNDRAISER_CODE;
    this.refs.studentInput.value = studentName;
    this.refs.successMessage.classList.remove('hidden');
    this.refs.errorMessage.classList.add('hidden');
    this.refs.studentField.classList.remove('hidden');
    this.#writeHiddenAttributes(FUNDRAISER_CODE, studentName);
    this.#syncCheckoutState();
    this.refs.studentInput.focus();
  }

  #setClearedUi(keepCodeValue = false) {
    this.dataset.applied = 'false';
    if (!keepCodeValue) this.refs.codeInput.value = '';
    this.refs.studentInput.value = '';
    this.refs.successMessage.classList.add('hidden');
    this.refs.studentField.classList.add('hidden');
    this.refs.studentError?.classList.add('hidden');
    this.#writeHiddenAttributes('', '');
    this.#syncCheckoutState();
  }

  #showUnrecognized() {
    this.refs.errorMessage.textContent = 'Fundraiser code not recognized.';
    this.refs.errorMessage.classList.remove('hidden');
    this.refs.successMessage.classList.add('hidden');
    this.refs.studentField.classList.add('hidden');
  }

  #hideMessages() {
    this.refs.errorMessage.classList.add('hidden');
    this.refs.studentError?.classList.add('hidden');
  }

  #writeHiddenAttributes(code, studentName) {
    if (this.refs.hiddenCode) {
      this.refs.hiddenCode.value = code;
      this.refs.hiddenCode.disabled = !code;
    }
    if (this.refs.hiddenStudent) {
      this.refs.hiddenStudent.value = studentName;
      this.refs.hiddenStudent.disabled = !code;
    }
  }

  #syncCheckoutState() {
    const blocked = (this.isApplied() || isValidFundraiserCode(this.getEnteredCode())) && !this.getStudentName();
    if (blocked) disableCheckoutButtons();
    else enableCheckoutButtons();
  }

  #onCartUpdate = () => {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#syncCheckoutState();
    });
  };
}

/**
 * @typedef {object} FundraiserBuyNowRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLInputElement} codeInput
 * @property {HTMLElement} successMessage
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} studentField
 * @property {HTMLInputElement} studentInput
 * @property {HTMLElement} [studentError]
 * @property {HTMLButtonElement} continueButton
 * @property {HTMLElement} [statusMessage]
 *
 * @extends {DialogComponent}
 */
class FundraiserBuyNowComponent extends DialogComponent {
  requiredRefs = ['dialog', 'codeInput', 'successMessage', 'errorMessage', 'studentField', 'studentInput', 'continueButton'];

  /** @type {HTMLFormElement | null} */
  #productForm = null;

  /** @type {boolean} */
  #applied = false;

  /** @type {boolean} */
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    bindCheckoutGuard();
    document.addEventListener('click', this.#onBuyNowClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onBuyNowClick, true);
  }

  applyCode = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#hideMessages();

    if (!isValidFundraiserCode(this.refs.codeInput.value)) {
      this.#applied = false;
      this.refs.errorMessage.classList.remove('hidden');
      this.refs.successMessage.classList.add('hidden');
      this.refs.studentField.classList.add('hidden');
      this.refs.studentInput.value = '';
      this.#syncContinueButton();
      return;
    }

    this.#applied = true;
    this.refs.codeInput.value = FUNDRAISER_CODE;
    this.refs.successMessage.classList.remove('hidden');
    this.refs.errorMessage.classList.add('hidden');
    this.refs.studentField.classList.remove('hidden');
    this.#syncContinueButton();
    this.refs.studentInput.focus();
  };

  handleCodeInput = () => {
    this.#hideMessages();
    if (this.#applied && !isValidFundraiserCode(this.refs.codeInput.value)) {
      this.#applied = false;
      this.refs.successMessage.classList.add('hidden');
      this.refs.studentField.classList.add('hidden');
      this.refs.studentInput.value = '';
      this.#syncContinueButton();
    }
  };

  handleStudentInput = () => {
    this.refs.studentError?.classList.add('hidden');
    this.#syncContinueButton();
  };

  continueToCheckout = (event) => {
    event.preventDefault();
    if (this.#busy) return;

    if (!this.#applied && isValidFundraiserCode(this.refs.codeInput.value)) {
      this.applyCode(event);
    }

    if (this.#applied && !this.refs.studentInput.value.trim()) {
      this.refs.studentError?.classList.remove('hidden');
      if (this.refs.studentError) this.refs.studentError.textContent = FUNDRAISER_STUDENT_REQUIRED_MESSAGE;
      this.refs.studentInput.focus();
      return;
    }

    const studentName = this.refs.studentInput.value.trim();
    const code = this.#applied ? FUNDRAISER_CODE : '';
    this.#goToCheckout(code, studentName);
  };

  skipFundraiser = (event) => {
    event.preventDefault();
    if (this.#busy) return;
    this.#goToCheckout('', '');
  };

  /**
   * @param {string} code
   * @param {string} studentName
   */
  async #goToCheckout(code, studentName) {
    this.#busy = true;
    this.refs.continueButton.disabled = true;
    this.refs.continueButton.textContent = 'Continuing to checkout…';

    try {
      await addProductAndCheckout(this.#productForm, code, studentName);
    } catch (error) {
      console.error(error);
      this.#busy = false;
      this.refs.continueButton.disabled = false;
      this.refs.continueButton.textContent = 'Continue to checkout';
      this.refs.errorMessage.classList.remove('hidden');
      this.refs.errorMessage.textContent = error instanceof Error ? error.message : 'Unable to continue to checkout. Please try again.';
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #onBuyNowClick = (event) => {
    if (this.#busy) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    const path = event.composedPath();
    if (path.some((node) => node instanceof Element && node.matches('shopify-accelerated-checkout-cart'))) {
      return;
    }

    const host = path.find((node) => {
      return (
        node instanceof Element &&
        (node.matches('.fundraiser-buy-now-catcher') ||
          node.matches('shopify-accelerated-checkout') ||
          node.matches('.shopify-payment-button') ||
          node.matches('.accelerated-checkout-block'))
      );
    });

    if (!(host instanceof Element)) return;
    if (host.closest('shopify-accelerated-checkout-cart, .cart-drawer, .cart-page, cart-items-component')) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.#productForm = host.closest('form') || document.querySelector('form[data-type="add-to-cart-form"]');
    this.#resetPopup();
    this.showDialog();
    this.#prefillFromCart();
  };

  async #prefillFromCart() {
    try {
      const cart = await fetchCart();
      const code = cart.attributes?.[ATTR_CODE] || '';
      const studentName = cart.attributes?.[ATTR_STUDENT] || '';
      if (isValidFundraiserCode(code)) {
        this.#applied = true;
        this.refs.codeInput.value = FUNDRAISER_CODE;
        this.refs.studentInput.value = studentName;
        this.refs.successMessage.classList.remove('hidden');
        this.refs.studentField.classList.remove('hidden');
      }
      this.#syncContinueButton();
    } catch (error) {
      console.error(error);
    }
  }

  #resetPopup() {
    this.#applied = false;
    this.#busy = false;
    this.refs.codeInput.value = '';
    this.refs.studentInput.value = '';
    this.refs.successMessage.classList.add('hidden');
    this.refs.errorMessage.classList.add('hidden');
    this.refs.errorMessage.textContent = 'Fundraiser code not recognized.';
    this.refs.studentField.classList.add('hidden');
    this.refs.studentError?.classList.add('hidden');
    this.refs.continueButton.disabled = false;
    this.refs.continueButton.textContent = 'Continue to checkout';
    this.#syncContinueButton();
  }

  #hideMessages() {
    this.refs.errorMessage.classList.add('hidden');
    this.refs.studentError?.classList.add('hidden');
    this.refs.errorMessage.textContent = 'Fundraiser code not recognized.';
  }

  #syncContinueButton() {
    const needsName = this.#applied && !this.refs.studentInput.value.trim();
    this.refs.continueButton.disabled = needsName || this.#busy;
  }
}

if (!customElements.get('fundraiser-code-component')) {
  customElements.define('fundraiser-code-component', FundraiserCodeComponent);
}

if (!customElements.get('fundraiser-buy-now-component')) {
  customElements.define('fundraiser-buy-now-component', FundraiserBuyNowComponent);
}

bindCheckoutGuard();

export { FundraiserCodeComponent, FundraiserBuyNowComponent };
